import type { AuthUser } from '@gb-ticket/shared-types';
import type { ComponentType } from 'react';

/** A card a product adds to `/profil` (e.g. Helpdesk connector pairing). */
export type ProfileSection = {
  id: string;
  /** Label in the profile sidebar; multiple cards may share one path. */
  navLabel: string;
  /** URL segment under `/profil`, e.g. `intranet`. */
  path: string;
  Component: ComponentType;
  isVisible: (user: AuthUser) => boolean;
};

export type ProfileSectionNavEntry = {
  path: string;
  navLabel: string;
};

const sections: ProfileSection[] = [];

/** Products register at import time; the platform never imports product code for this. */
export function registerProfileSection(section: ProfileSection): void {
  const index = sections.findIndex((item) => item.id === section.id);
  if (index >= 0) {
    sections[index] = section;
    return;
  }
  sections.push(section);
}

export function profileSectionsFor(user: AuthUser | null | undefined): ProfileSection[] {
  return user ? sections.filter((section) => section.isVisible(user)) : [];
}

/** One sidebar row per distinct `path` among visible product sections. */
export function profileSectionNavEntriesFor(
  user: AuthUser | null | undefined,
): ProfileSectionNavEntry[] {
  const entries: ProfileSectionNavEntry[] = [];
  const seen = new Set<string>();
  for (const section of profileSectionsFor(user)) {
    if (seen.has(section.path)) {
      continue;
    }
    seen.add(section.path);
    entries.push({ path: section.path, navLabel: section.navLabel });
  }
  return entries;
}

export function profileSectionsForPath(
  user: AuthUser | null | undefined,
  path: string,
): ProfileSection[] {
  return profileSectionsFor(user).filter((section) => section.path === path);
}
